import {ModelSetOptions} from 'backbone';

import BenchlingConfig from 'BenchlingConfig';
import {BufferViewLayout} from 'buffers/BufferComps';
import {Sequence} from 'components/Sequence';
import {CircularMapState} from 'molbio/maps/MapStates';
import SinglePolymerMapTabState, {
  SinglePolymerMapTabStateAttributes,
} from 'molbio/tabstates/SinglePolymerMapTabState';
import {TabStateDefaults} from 'Navigation';
import {
  constructOptions,
  MakeLocalSyncOptions,
  VisibilityOptions,
} from 'settings/visibility-options/VisibilityOptions';
import * as vo from 'settings/visibility-options/VisibilityOptions';

interface CircularMapTabStateAttributes
  extends SinglePolymerMapTabStateAttributes<Sequence, CircularMapState> {
  snapshot: boolean;
  bufferViewLayout: BufferViewLayout | null;
  visibilityOptions: VisibilityOptions;
}

interface CircularMapTabState {
  get<K extends keyof CircularMapTabStateAttributes>(key: K): CircularMapTabStateAttributes[K];

  set<K extends keyof CircularMapTabStateAttributes>(
    attributeName: K,
    value: CircularMapTabStateAttributes[K],
    options?: ModelSetOptions
  ): CircularMapTabState;

  set(obj: Partial<CircularMapTabStateAttributes>): CircularMapTabState;
}

class CircularMapTabState extends SinglePolymerMapTabState<Sequence, CircularMapState> {
  mapState(options: {polymer: Sequence; visibilityOptions: VisibilityOptions}): CircularMapState {
    return new CircularMapState(options);
  }

  getVisibilityOptions(): VisibilityOptions {
    return this.get('visibilityOptions');
  }

  msVisibilityOptions(options?: MakeLocalSyncOptions): VisibilityOptions {
    const transcriptionLinksPresent = this.sequence()._sequence().tagSchemaHasTranscriptionLinkFields();
    const visibilityOptions = this.get('visibilityOptions');
    if (!transcriptionLinksPresent) {
      visibilityOptions.remove(visibilityOptions.where({name: vo.TRANSCRIPTIONS.name}));
    }
    options = {};

    if (this.get('snapshot')) {
      // Don't sync with the tabstate's VisibilityOptions,
      // ...which get pref sync'd.
      options.globalSync = false;
    }

    const msVo = visibilityOptions.makeLocalSync(options);

    if (this.get('snapshot')) {
      // Explicititly set which options are visible.
      for (const option of [
        vo.ANNOTATIONS,
        vo.TRANSLATIONS,
        vo.PRIMERS,
        vo.SEQUENCE_AXIS,
        vo.INLINE_LABELS,
      ]) {
        msVo.getOption(option.name)!.set('visible', true);
      }
      for (const option of [vo.ORFS, vo.CUTSITES, vo.SEARCH_BASES]) {
        msVo.getOption(option.name)!.set('visible', false);
      }
    }

    return msVo;
  }

  override defaults(): TabStateDefaults & Partial<CircularMapTabStateAttributes> {
    return {
      ...super.defaults(),
      title: 'Plasmid',
      closeable: false,
      snapshot: false,
      bufferViewLayout: null,
      visibilityOptions: constructOptions('circular', [
        {base: vo.ANNOTATIONS, visible: true, editable: true, sync: true},
        {base: vo.TRANSLATIONS, visible: true, editable: true, sync: true},
        ...(BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS
          ? [
              {
                base: vo.PART_ANNOTATIONS,
                visible: true,
                editable: true,
                sync: true,
              },
            ]
          : []),
        ...(BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS &&
        BenchlingConfig.MOLBIO_ENABLE_EDITING_BIOINTELLIGENT_LINKS
          ? [
              {
                base: vo.TRANSCRIPTIONS,
                visible: true,
                editable: true,
                sync: true,
              },
            ]
          : []),
        {base: vo.PRIMERS, visible: true, editable: true, sync: true},
        {base: vo.CUTSITES, visible: true, editable: true, sync: true},
        {base: vo.ORFS, visible: false, editable: true, sync: true},
        {base: vo.SEQUENCE_AXIS, visible: true, editable: true, sync: true},
        {base: vo.HOMOLOGY_REGION_AXIS, visible: false, editable: false, sync: false},
        {base: vo.SEARCH_BASES, visible: true, editable: false, sync: true},
        {base: vo.INLINE_LABELS, visible: true, editable: true, sync: true},
        {base: vo.PARTS, visible: true, editable: false},
        {base: vo.DNA, visible: true, editable: false, sync: false},
        {base: vo.HOMOLOGY_REGION_DNA, visible: false, editable: false, sync: false},
      ]),
      preferenceKey: 'circular-map',
    };
  }
}

export default CircularMapTabState;
